import { Box, Divider, Link } from '@chakra-ui/react';

export const LongTermMarketingContent = () => (
  <Box>
    <Box mb={4}>
      In an era obsessed with instant metrics and rapid feedback loops,
      marketers often prioritize short-term returns such as clicks, conversions,
      and immediate revenue. But as honesty demands, this myopic focus on the
      short term can undermine brand strength and long-term growth.
    </Box>
    <Box mb={4}>
      Enter the research of Les Binet and Peter Field, the most respected voices
      in modern marketing effectiveness.{' '}
      <Link
        href="https://www.amazon.co.uk/Long-Short-Balancing-Long-Term-Strategies/dp/085294134X"
        color="blue.500"
        fontWeight="medium"
        _hover={{ textDecoration: 'underline' }}
        isExternal>
        (Long-Short-Balancing-Long-Term-Strategies).
      </Link>{' '}
      Their work reminds us: marketing is a marathon, not a sprint. And if we
      don&apos;t balance short-term activation with sustained brand-building, we
      jeopardize future success. Let&apos;s explore how and why.
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        The Hidden Cost of Short-Termism
      </Box>
      <Box mb={4}>
        Digital platforms offer granular, real-time visibility into campaign
        performance. It&apos;s tempting to chase quick wins such as promotions,
        search ads, performance activation. But heavy reliance on these tactics
        comes at the expense of brand health.
      </Box>
      <Box mb={4}>As Binet explains:</Box>
      <Box mb={4} fontStyle="italic">
        &ldquo;There are two ways to sell things … activation … doesn&apos;t
        require building up long-term memories … Brand building is about
        building up these mental structures … that will bias their behavior into
        the future.&rdquo;{' '}
        <Link
          href="https://www.warc.com/newsandopinion/opinion/les-binet-on-why-long-term-marketing-matters-in-the-age-of-short-termism/en-gb/3307"
          color="blue.500"
          fontWeight="medium"
          fontStyle="normal"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          warc.com
        </Link>
      </Box>
      <Box mb={4}>
        Peter Field echoes the concern, noting that the rise of activation-first
        tactics has led to declining campaign effectiveness overall:
      </Box>
      <Box mb={4} fontStyle="italic">
        &ldquo;Short-termism is undermining long-term success&rdquo;{' '}
        <Link
          href="https://advertisingcouncil.org.au/news/peter-field-short-termism-is-undermining-long-term-success-in-advertising/"
          fontStyle="normal"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          Advertising Council Australia
        </Link>
      </Box>
      <Box mb={4}>
        When MMMs (Marketing Mix Models) focus only on short-term sales lift,
        they risk misleading conclusions and misallocation of the marketing
        budget.
      </Box>
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        The &ldquo;60:40 Rule&rdquo; – Finding the Balance
      </Box>
      <Box mb={4}>
        In their landmark IPA report The Long and the Short of It, Binet & Field
        recommend a strategic mix: 60% of marketing budget should go to brand
        building, and 40% to activation. This split strikes the ideal balance
        between building emotional, memory structures and driving immediate ROI.{' '}
        <Link
          href="https://system1group.com/blog/the-long-short-of-it"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          System1 Group
        </Link>{' '}
        <Link
          href="https://www.rainforgrowth.com/insights-updates/ten-years-later-the-long-and-short-of-it-research-still-rings-true-today/"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          Rain the Growth Agency
        </Link>
      </Box>
      <Box mb={4}>
        Over-reliance on activation may yield short-lived spikes, but brands
        that invest in brand-building enjoy sustained growth, higher margins,
        and long-term preference. Emotional ads don&apos;t just deliver growth,
        they also amplify activation&apos;s effectiveness.{' '}
        <Link
          href="https://www.rainforgrowth.com/insights-updates/ten-years-later-the-long-and-short-of-it-research-still-rings-true-today/"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          Rain the Growth Agency
        </Link>{' '}
        <Link
          href="https://www.warc.com/newsandopinion/opinion/les-binet-on-why-long-term-marketing-matters-in-the-age-of-short-termism/en-gb/3307"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          warc.com
        </Link>
      </Box>
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Why MMM Often Misses the Full Picture
      </Box>
      <Box mb={4}>
        Traditional MMMs are incredibly useful, yet inherently biased toward
        short-term response:
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          They favor time-specific, measurable media (like promotions or flash
          campaigns) over brand-building assets whose impact unfolds longer.
        </Box>
        <Box as="li" mb={2}>
          They often exclude brand equity from their calculations, although
          equity strongly influences future performance.{' '}
          <Link
            href="https://en.wikipedia.org/wiki/Marketing_mix_modeling"
            color="blue.500"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
            isExternal>
            Wikipedia
          </Link>
        </Box>
      </Box>
      <Box mb={4}>
        MMM may show that promotions yield high ROI, but if those promotions
        cannibalize brand equity, long-term profitability suffers. True Return
        on Marketing Investment (ROMI) includes both short-term lift and
        long-term brand impact.
      </Box>
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        How Merkatus.io Differs from Other Marketing Effectiveness Tools
      </Box>
      <Box mb={4}>
        At Merkatus.io, we approach effectiveness differently. Instead of
        relying on a single ROI lens, we run two complementary algorithms: one
        measuring short-term activation response, and another capturing
        long-term brand effects.
      </Box>
      <Box mb={4}>
        We then combine these into a single, holistic view of marketing
        effectiveness. This gives you confidence that you&apos;re not
        cannibalizing brand equity by over-investing in easily measured
        short-term channels.
      </Box>
      <Box mb={4}>
        The result: your{' '}
        <span style={{ fontStyle: 'italic' }}>
          true Return on Marketing Investment (ROMI)
        </span>
        : activation + brand, immediate + sustained and delivered using the data
        you already have, with setup in hours, not months.
      </Box>
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        The Business Case: Long-Term Wins Make Short-Term Better
      </Box>
      <Box mb={4}>
        Ironically, brand-building not only supports long-term growth, but it
        also supercharges short-term activation.
      </Box>
      <Box mb={4}>
        System1&apos;s analysis finds that in over 90% of cases, ads built for
        long-term impact (high brand equity) also delivered above-average
        short-term sales spikes. Long-term storytelling drives short-term
        results—making brand-building the ultimate &ldquo;strategic BOGOF&rdquo;
        (buy one, get one free).{' '}
        <Link
          href="https://system1group.com/blog/the-long-short-of-it"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          System1 Group
        </Link>{' '}
        <Link
          href="https://www.warc.com/content/feed/long-term-brand-building-is-the-ultimate-strategic-bogof/en-GB/7765"
          color="blue.500"
          fontWeight="medium"
          _hover={{ textDecoration: 'underline' }}
          isExternal>
          warc.com
        </Link>
      </Box>
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        What Marketers Can Do Today: A Balanced, Holistic Approach
      </Box>
      <Box as="ol" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <strong>Reframe measurement:</strong> Talk in terms of cashflow over
          time, not just conversions today. This resonates with CFOs and shifts
          the conversation from &ldquo;fluffy&rdquo; to financial impact.{' '}
          <Link
            href="https://www.warc.com/newsandopinion/opinion/les-binet-on-why-long-term-marketing-matters-in-the-age-of-short-termism/en-gb/3307"
            fontStyle="normal"
            color="blue.500"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
            isExternal>
            warc.com
          </Link>
        </Box>
        <Box as="li" mb={2}>
          <strong>Allocate smartly:</strong> Start with the 60/40 heuristic,
          then fine-tune based on your industry, growth stage, and pricing
          structure.{' '}
          <Link
            href="https://www.warc.com/newsandopinion/opinion/les-binet-on-why-long-term-marketing-matters-in-the-age-of-short-termism/en-gb/3307"
            fontStyle="normal"
            color="blue.500"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
            isExternal>
            warc.com
          </Link>{' '}
          <Link
            href="https://system1group.com/blog/the-long-short-of-it"
            fontStyle="normal"
            color="blue.500"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
            isExternal>
            System1 Group
          </Link>
        </Box>
        <Box as="li" mb={2}>
          <strong>Upgrade MMM:</strong> Layer brand equity measurement (e.g.,
          awareness, consideration metrics) or augment MMM with a multi-stage
          model that tracks how brand health translates to sales.{' '}
          <Link
            href="https://marketbridge.com/article/measuring-the-long-term-impact-of-brand-investments/"
            color="blue.500"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
            isExternal>
            Marketbridge
          </Link>
        </Box>
        <Box as="li" mb={2}>
          <strong>Use emotional storytelling:</strong> Invest in creative,
          emotionally resonant ads, not only for long-term memory-building but
          because they also deliver short-term activation lifts.{' '}
          <Link
            href="https://www.rainforgrowth.com/insights-updates/ten-years-later-the-long-and-short-of-it-research-still-rings-true-today/"
            color="blue.500"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
            isExternal>
            Rain the Growth Agency
          </Link>
        </Box>
      </Box>
    </Box>
    <Divider my={12} />
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Final Thoughts
      </Box>
      <Box mb={4}>
        In the race for immediate ROI, it&apos;s easy to overlook the power of
        long-term brand-building. But as Les Binet and Peter Field strongly
        remind us, sustainable growth depends on building mental structures and
        brand equity, across time and touch points.
      </Box>
      <Box mb={4}>
        By balancing activation with long-term brand investment, and by properly
        measuring both, marketers can create strategies that not only sell today
        but continue to sell tomorrow.
      </Box>
    </Box>
  </Box>
);
